import React, { useCallback, useEffect, useState } from 'react';
import { Card } from 'primereact/card';
import { Form, Formik } from 'formik';

import { fetchData } from '../../../../helpers/fetchData';
import { labModuleApi } from '../../../../helpers/labModuleApi';
import SkeletonCardLoading from '../../../../shared/SkeltonCardLoading';
import {
  formatedGeneralElementsData,
  renderFormFields,
  renderFormSubCategoryRows
} from '../../../../constants/FormCreationConstants';
import CustomButton from '../../../../shared/CustomButton';
import toast from 'react-hot-toast';
import NoDataFound from '../../../../shared/NoDataFound';
import CheckPermission from '../../../../hooks/CheckPermission';

const IpVisitFormResults = ({ selectedCategoryId, visitId, selectedFormType }) => {
  const { hasPermission } = CheckPermission();
  const [isLoading, setIsLoading] = useState(false);
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);
  const [formFields, setFormFields] = useState({
    formHeader: [],
    formBody: [],
    formFooter: []
  });

  const getFormData = useCallback(async () => {
    setIsLoading(true);
    const formResponse = await fetchData(
      'GET',
      labModuleApi.categories.getById(selectedCategoryId)
    );
    formatedGeneralElementsData(formResponse, setFormFields);
    setIsLoading(false);
  }, [selectedCategoryId]);

  useEffect(() => {
    getFormData();
  }, [getFormData, selectedCategoryId]);

  const getInitialValues = () => {
    const initialValues = {};

    const allFields = [
      ...(formFields?.formHeader || []),
      ...(formFields?.formBody || []),
      ...(formFields?.formFooter || [])
    ];

    allFields.forEach(section => {
      if (section.elements && Array.isArray(section.elements)) {
        section.elements.forEach(field => {
          initialValues[field.id] = field.type === 'bool' ? false : field.value || '';
        });
      }
    });
    return initialValues;
  };

  const handleFormSubmit = async (values, { resetForm }) => {
    if (!hasPermission('forms-creation', 'create')) return;
    const header = formFields?.formHeader
      ?.map(data => {
        if (data?.elements?.length) {
          return {
            ...data,
            elements: data.elements.map(element => ({
              ...element,
              value: { value: values[element?.id] }
            }))
          };
        }
        return null;
      })
      ?.filter(data => data !== null);
    const body = formFields?.formBody
      ?.map(data => {
        if (data?.elements?.length) {
          return {
            ...data,
            elements: data.elements.map(element => ({
              ...element,
              value: { value: values[element?.id] }
            }))
          };
        }
        return null;
      })
      ?.filter(data => data !== null);
    const footer = formFields?.formFooter
      ?.map(data => {
        if (data?.elements?.length) {
          return {
            ...data,
            elements: data.elements.map(element => ({
              ...element,
              value: { value: values[element?.id] }
            }))
          };
        }
        return null;
      })
      ?.filter(data => data !== null);

    const dataToSubmit = {
      item_id: String(visitId),
      data: {
        header,
        body,
        footer
      }
    };
    setSubmitButtonLoading(true);
    const response = await fetchData(
      'POST',
      labModuleApi.visitResults.create(),
      dataToSubmit
    );
    if (response?.data) {
      toast.success(response?.message);
      resetForm();
    }
    setSubmitButtonLoading(false);
  };

  return (
    <Card pt={{ root: { className: 'px-0 h-fit' }, body: { className: 'px-0' } }}>
      <div className='flex items-center justify-between px-3 border-b border-gray-300 pb-3'>
        <h6 className='text-20-gray !font-semibold'>Results</h6>
      </div>
      {isLoading ? (
        <SkeletonCardLoading count={10} />
      ) : (
        <div className='max-h-[400px] lg:max-[500px] xl:max-h-[500px] overflow-y-auto px-3 mt-4'>
          {formFields?.formHeader?.length || formFields?.formBody?.length || formFields?.formFooter?.length ? (
            <Formik enableReinitialize={true} initialValues={getInitialValues()} onSubmit={handleFormSubmit}>
              {props => (
                <Form>
                  <div className='form-container'>
                    {formFields?.formHeader &&
                      formFields?.formHeader?.map(data => renderFormSubCategoryRows(data, renderFormFields))}
                    {formFields?.formBody &&
                      formFields?.formBody?.map(data => renderFormSubCategoryRows(data, renderFormFields))}
                    {formFields?.formFooter &&
                      formFields?.formFooter?.map(data => renderFormSubCategoryRows(data, renderFormFields))}
                  </div>
                  <div className='flex items-center justify-center mb-3 mt-2'>
                    {hasPermission('forms-creation', 'create') && (
                    <CustomButton
                      loading={submitButtonLoading}
                      type='submit'
                      label='Save Results'
                      buttonType='primary'
                    />
                    )}
                  </div>
                </Form>
              )}
            </Formik>
          ) : (
            <div className='mt-4'>
              <NoDataFound iconSize='30' titleClassName='text-16-dark font-semibold' />
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default IpVisitFormResults;
